import api from './api';

// ============================================
// Types
// ============================================

export interface AvailableEdge {
  id: string;
  name: string;
  serial_number: string;
  wan_ip?: string;
  type: 'dedicated' | 'mtge';
}

export interface Connector {
  id: string;
  serial_number: string;
  tenant_id: string;
  hub_edge_id?: string;
  hub_edge_serial?: string;
  hub_edge_name?: string;
  hub_type?: 'dedicated' | 'mtge';
  hub_mtge_id?: string;
  connector_name: string;
  description?: string;
  device_model: string;
  firmware_version?: string;
  agent_version?: string;
  wan_ip?: string;
  deployment_region?: string;
  status: 'pending_approval' | 'approved' | 'provisioned' | 'active' | 'offline' | 'decommissioned';
  auto_approve: number;
  sequence_number: number;
  last_config_hash?: string;
  is_dirty: number;
  dirty_reason?: string;
  sync_status: 'synced' | 'pending' | 'applying' | 'failed' | 'unknown';
  last_error?: string;
  last_seen?: string;
  approved_by?: string;
  approved_at?: string;
  registered_at?: string;
  created_at?: string;
  updated_at?: string;
  metrics_enabled?: number;
  metrics_ip?: string;
  metrics_port?: number;
  lan_subnets?: string[] | null;
  app_vpn_allowed_ips?: string[] | null;
}

export interface ConnectorWireguard {
  id: string;
  connector_id: string;
  interface_name: string;
  listen_port: number;
  public_key: string;
  server_address: string;
  mtu: number;
  endpoint_host?: string;
  enabled: number;
  status: string;
  created_at?: string;
  updated_at?: string;
}

export interface ConnectorRoute {
  id: string;
  connector_id: string;
  destination: string;
  gateway?: string;
  interface_name?: string;
  metric: number;
  description?: string;
  enabled: number;
  created_at?: string;
  updated_at?: string;
}

export interface ConnectorNat {
  id: string;
  connector_id: string;
  enabled: number;
  masquerade_interface: string;
  source_ranges?: string[] | string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ConnectorAcl {
  id: string;
  connector_id: string;
  rule_name: string;
  priority: number;
  direction: 'inbound' | 'outbound';
  source_cidr?: string;
  dest_cidr?: string;
  protocol?: string;
  port_range?: string;
  action: 'allow' | 'deny';
  enabled: number;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateConnectorData {
  connectorName: string;
  tenantId: string;
  description?: string;
  deviceModel?: string;
  deploymentRegion?: string;
  wanIp?: string;
  hubEdgeId?: string;
  hubType?: 'dedicated' | 'mtge';
  autoApprove?: boolean;
}

export interface UpdateConnectorData {
  connectorName?: string;
  description?: string;
  wanIp?: string;
  lanSubnets?: string[] | null;
  hubEdgeId?: string;
  hubType?: 'dedicated' | 'mtge';
  deploymentRegion?: string;
  metricsEnabled?: number;
  metricsIp?: string;
  metricsPort?: number;
}

export interface UpdateWireguardData {
  listenPort?: number;
  mtu?: number;
  endpointHost?: string;
  enabled?: boolean;
}

export interface CreateRouteData {
  destination: string;
  gateway?: string;
  interfaceName?: string;
  metric?: number;
  description?: string;
  enabled?: boolean;
}

export interface UpdateRouteData {
  destination?: string;
  gateway?: string;
  interfaceName?: string;
  metric?: number;
  description?: string;
  enabled?: boolean;
}

export interface UpdateNatData {
  enabled?: boolean;
  masqueradeInterface?: string;
  sourceRanges?: string[];
}

export interface CreateAclData {
  ruleName: string;
  priority?: number;
  direction: 'inbound' | 'outbound';
  sourceCidr?: string;
  destCidr?: string;
  protocol?: string;
  portRange?: string;
  action?: 'allow' | 'deny';
  enabled?: boolean;
  description?: string;
}

export interface UpdateAclData {
  ruleName?: string;
  priority?: number;
  direction?: 'inbound' | 'outbound';
  sourceCidr?: string;
  destCidr?: string;
  protocol?: string;
  portRange?: string;
  action?: 'allow' | 'deny';
  enabled?: boolean;
  description?: string;
}

export interface ConnectorListParams {
  status?: string;
  tenantId?: string;
  limit?: number;
  offset?: number;
}

export interface PaginatedConnectorResponse {
  items: Connector[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================
// Service
// ============================================

const connectorService = {
  // ── Device CRUD ─────────────────────────────

  list: async (params?: ConnectorListParams) => {
    const response = await api.get('/api/v2/connectors', { params }) as any;
    return response.data as PaginatedConnectorResponse;
  },

  getById: async (id: string) => {
    const response = await api.get(`/api/v2/connectors/${id}`) as any;
    return response.data as Connector;
  },

  create: async (data: CreateConnectorData) => {
    const response = await api.post('/api/v2/connectors', data) as any;
    return response.data;
  },

  update: async (id: string, data: UpdateConnectorData) => {
    const response = await api.put(`/api/v2/connectors/${id}`, data) as any;
    return response.data as Connector;
  },

  approve: async (id: string) => {
    const response = await api.post(`/api/v2/connectors/${id}/approve`) as any;
    return response.data;
  },

  decommission: async (id: string) => {
    await api.delete(`/api/v2/connectors/${id}`);
  },

  syncConfig: async (id: string) => {
    const response = await api.post(`/api/v2/connectors/${id}/sync`) as any;
    return response.data as { seqNum: number; configHash: string; commandCount: number; topic: string };
  },

  getAvailableEdges: async (tenantId: string, excludeConnectorId?: string) => {
    const params: Record<string, string> = { tenantId };
    if (excludeConnectorId) params.excludeConnectorId = excludeConnectorId;
    const response = await api.get('/api/v2/connectors/available-edges', { params }) as any;
    return response.data as AvailableEdge[];
  },

  // ── WireGuard ───────────────────────────────

  getWireguard: async (id: string) => {
    const response = await api.get(`/api/v2/connectors/${id}/wireguard`) as any;
    return response.data as ConnectorWireguard;
  },

  updateWireguard: async (id: string, data: UpdateWireguardData) => {
    const response = await api.put(`/api/v2/connectors/${id}/wireguard`, data) as any;
    return response.data as ConnectorWireguard;
  },

  regenerateKeys: async (id: string) => {
    const response = await api.post(`/api/v2/connectors/${id}/wireguard/regenerate-keys`) as any;
    return response.data as { publicKey: string; message: string };
  },

  // ── Routes ──────────────────────────────────

  listRoutes: async (id: string) => {
    const response = await api.get(`/api/v2/connectors/${id}/routes`) as any;
    return response.data as ConnectorRoute[];
  },

  addRoute: async (id: string, data: CreateRouteData) => {
    const response = await api.post(`/api/v2/connectors/${id}/routes`, data) as any;
    return response.data;
  },

  updateRoute: async (id: string, routeId: string, data: UpdateRouteData) => {
    const response = await api.put(`/api/v2/connectors/${id}/routes/${routeId}`, data) as any;
    return response.data as ConnectorRoute;
  },

  deleteRoute: async (id: string, routeId: string) => {
    await api.delete(`/api/v2/connectors/${id}/routes/${routeId}`);
  },

  // ── NAT ─────────────────────────────────────

  getNat: async (id: string) => {
    const response = await api.get(`/api/v2/connectors/${id}/nat`) as any;
    return response.data as ConnectorNat;
  },

  updateNat: async (id: string, data: UpdateNatData) => {
    const response = await api.put(`/api/v2/connectors/${id}/nat`, data) as any;
    return response.data as ConnectorNat;
  },

  // ── ACL ─────────────────────────────────────

  listAcl: async (id: string) => {
    const response = await api.get(`/api/v2/connectors/${id}/acl`) as any;
    return response.data as ConnectorAcl[];
  },

  addAcl: async (id: string, data: CreateAclData) => {
    const response = await api.post(`/api/v2/connectors/${id}/acl`, data) as any;
    return response.data;
  },

  updateAcl: async (id: string, ruleId: string, data: UpdateAclData) => {
    const response = await api.put(`/api/v2/connectors/${id}/acl/${ruleId}`, data) as any;
    return response.data as ConnectorAcl;
  },

  deleteAcl: async (id: string, ruleId: string) => {
    await api.delete(`/api/v2/connectors/${id}/acl/${ruleId}`);
  },
};

export default connectorService;
